import React, { useEffect, useRef, useState } from "react";

import styles from "../styles/selectLocationModal.module.css";
import { useNavigate } from "react-router-dom";
import Table from "./table";
import {
  getLocationList,
  switchAccountLocation,
} from "../services/admin/organization";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

const SelectLocationModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const [currentLocation, setCurrentLocation] = useState(
    JSON.parse(sessionStorage.getItem("current_loc")) || []
  );

  const {
    data: locationList,
    isLoading: isLocationsLoading,
    refetch: refetchLocation,
  } = useQuery(["locations-list"], () => getLocationList());

  const sortedLocations = locationList?.locations
    ?.map((loc) => ({
      ...loc,
      // id: loc.id,
      name: loc.id === currentLocation?.id ? `${loc.name} (Active)` : loc.name,
      // staff_count: loc.staff_count,
      // patients_count: loc.patients_count,
      isCurrent: String(loc.id) === String(currentLocation?.id),
    }))
    .sort((a, b) => (a.isCurrent ? -1 : b.isCurrent ? 1 : 0))
    .map(({ isCurrent, ...rest }) => rest);

  const switchLocationMutation = useMutation(
    (data) => switchAccountLocation(data),
    {
      onError: (error) => {
        const status = error?.response?.status;
        if (status === 403 || status === 401) {
          toast.error("You don't have access to this location.");
          return;
        }
        toast.error(error?.response?.data?.error || "Error switching location");
      },
    }
  );

  const handleChangeLocation = async (location) => {
    if (currentLocation?.id === location.id) {
      toast.error(`Location already active`);
      return;
    }

    // Snapshot for rollback on API failure (mirrors the pattern in switchModal.jsx).
    const prevCurrentLocation = currentLocation;
    const prevUser = localStorage.getItem("user");
    const prevFallbackLocation = localStorage.getItem("fallback_location");
    const prevCurrentLoc = sessionStorage.getItem("current_loc");

    const rollback = () => {
      setCurrentLocation(prevCurrentLocation);
      if (prevUser !== null) localStorage.setItem("user", prevUser);
      else localStorage.removeItem("user");
      if (prevFallbackLocation !== null)
        localStorage.setItem("fallback_location", prevFallbackLocation);
      else localStorage.removeItem("fallback_location");
      if (prevCurrentLoc !== null)
        sessionStorage.setItem("current_loc", prevCurrentLoc);
      else sessionStorage.removeItem("current_loc");
    };

    // Optimistic update — committed on success, reverted on failure.
    setCurrentLocation(location);

    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      user.current_location = location?.id;
      localStorage.setItem("user", JSON.stringify(user));
    }

    localStorage.setItem("fallback_location", JSON.stringify(location));
    sessionStorage.setItem("current_loc", JSON.stringify(location));

    const acc_id = sessionStorage.getItem("acc_id");

    const data = {
      account_id: acc_id,
      location_id: String(location?.id),
    };

    try {
      await switchLocationMutation.mutateAsync(data);
    } catch {
      rollback();
      // onError in mutation config already shows toast
      return;
    }
    queryClient.clear();
    onClose();
    // Hard reload — ensures every singleton/cache rehydrates with the new location context.
    window.location.href = "/dashboard";
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const locationTableColumns = [
    { key: "name", label: "Location", alignLeft: true },
    { key: "patients_count", label: "Total Patients" },
    { key: "active_patients", label: "Active Patients" },
    // { key: "inactive_patients", label: "Inactive Patients" },
    { key: "staff_count", label: "Staff Count" },
  ];

  return (
    <div className={styles.overlay}>
      <div
        className={styles.modal}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="select-location-modal-title"
      >
        <div className={styles.comp_header}>
          <p id="select-location-modal-title" className={styles.comp_heading}>
            Select Location To Switch To
          </p>
        </div>

        <div className={styles.comp_wrapper}>
          <p>Click the location you would like to switch to</p>

          <div className={styles.table_wrapper}>
            <Table
              columns={locationTableColumns}
              // data={locationList?.locations}
              data={sortedLocations ?? []}
              loading={isLocationsLoading}
              height={250}
              rowClass={(row) =>
                row.id === currentLocation?.id ? "selected_loc_row" : ""
              }
              isRowClickable={true}
              onRowClick={(location) => handleChangeLocation(location)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectLocationModal;
